/**
 * verification — что заключила проверка фактов.
 *
 * Зеркалит `VerificationNotice.tsx` и условие показа в AnalysisPanel.tsx v2:
 * пусто, только когда состояния нет ИЛИ гейт открыт и замечаний не осталось.
 * После #752 нарекание к прозе гейт не закрывает, и `verified` сам по себе
 * молчания не означает. Заголовок берётся из общего словаря
 * (`lib/records/verification-title.ts`) — три состояния, названные одинаково на
 * экране и в документе. Своя копия ветвления была здесь третьей по счёту, и
 * четвёртое состояние доехало бы до копий поодиночке.
 *
 * **Печать сознательно информативнее экрана** (как `compliance.notChecked` и
 * `dialogue.closingNotApplicable`), и после #752 — заметно: на экране весь
 * разбор лежит за значком и раскрывается нажатием, в документе он раскрыт
 * всегда. PDF читают вне приложения, где нажимать нечего, а отчёт без
 * предупреждения выглядит проверенным.
 *
 * Подсказку «сверьте с расшифровкой» документ не печатает: она отсылает к
 * экрану, а тест «никогда не выводит транскрипт» запрещает само слово в
 * разметке — и правильно, документ не должен намекать, что несёт расшифровку.
 */
import { tag } from '../primitives';
import { escapeHtml } from '../escape';
import { NARRATIVE_SECTION_LABEL_KEYS } from '@/lib/records/narrative-sections';
import {
  isVerificationWithheld,
  verificationTitle,
} from '@/lib/records/verification-title';
import type { SectionFn } from '../types';

export const renderVerification: SectionFn = (input, t) => {
  const verification = input.analysis.verification;
  if (!verification) return '';

  /**
   * Два повода печатать, а не один: гейт закрыт ИЛИ верификатор возражал против
   * отдельных утверждений. Второй появился с #752 — нарекание к прозе больше не
   * закрывает автоматизацию, и анализ бывает `verified` с замечаниями. Ранний
   * выход по статусу стирал бы их из документа, а документ читают вне
   * приложения: раскрыть там нечего, и отчёт выглядел бы подтверждённым целиком.
   */
  const withheld = isVerificationWithheld(verification.verificationStatus);
  if (!withheld && verification.narrativeIssues.length === 0) return '';

  const title = verificationTitle(verification.verificationStatus, t);

  const reasons = verification.reviewReasonCodes
    .map((code) => t(`common:reviewQueue.reasons.${code}`, code))
    .join(' · ');

  const facts = verification.questionedFacts
    .map((fact) => {
      const removed =
        fact.outcome === 'removed'
          ? ` — ${t('records.verificationNotice.questionedFactRemoved')}`
          : '';
      return `<p>${escapeHtml(`• ${fact.text}${removed}`)}</p>`;
    })
    .join('');

  const issues = verification.narrativeIssues
    .map((issue, index) => {
      const isLast = index === verification.narrativeIssues.length - 1;
      const sectionKey = NARRATIVE_SECTION_LABEL_KEYS.get(issue.sectionId);
      const section = sectionKey ? t(sectionKey) : issue.sectionId;
      return (
        `<div class="box"${isLast ? '' : ' style="margin-bottom:6px"'}>` +
        `<div class="top">${tag(t(`records.verificationNotice.issueTypes.${issue.issueType}`), 'muted')}` +
        `<b>${escapeHtml(section)}</b></div>` +
        `<p>${escapeHtml(issue.draftExcerpt)}</p>` +
        (issue.evidenceQuote ? `<q>${escapeHtml(issue.evidenceQuote)}</q>` : '') +
        `</div>`
      );
    })
    .join('');

  return (
    `<div class="flag"><b>${escapeHtml(title)}</b>` +
    (reasons ? `<p>${escapeHtml(reasons)}</p>` : '') +
    // «Верификация удержала автоматические изменения в CRM» — только когда
    // удержала. При открытом гейте поля заполнены и задачи созданы, и та же
    // фраза стала бы прямой неправдой о записи клиента.
    // Блок только по фактам удержал одни поля (#3157): текст про задачи был бы неправдой.
    (withheld
      ? `<p>${escapeHtml(
          verification.crmFieldsOnlyHold
            ? t('records.verificationNotice.bodyFactsOnly')
            : t('records.verificationNotice.body')
        )}</p>`
      : '') +
    (facts
      ? `<p><b>${escapeHtml(t('records.verificationNotice.questionedFacts'))}</b></p>${facts}`
      : '') +
    (issues ? `<div style="margin-top:8px">${issues}</div>` : '') +
    `</div>`
  );
};
